/**
 * Reiner Serializer-Kern des Rich-Text-Composers (P1.10).
 *
 * Bewusst OHNE Laufzeit-Importe (kein DOMPurify, kein React) → in reinem Node
 * testbar (``node --experimental-strip-types``). Die DOM-/DOMPurify-abhängigen
 * Wrapper (``sanitizeEditorHtml`` / ``spliceSignatureHtml``) leben in ``Mail.tsx``
 * und komponieren diese puren Bausteine.
 *
 * Vertrag: der Composer-``body``-State ist gewhitelistetes Editor-HTML (P0.4/P1.8
 * geteilte Quelle). Markdown-Seeds (KI-Vorschlag, Vorlage) werden über
 * ``mdToHtml`` in genau diese Whitelist-Form gebracht, bevor sie den Editor füllen.
 */

/** Erlaubte Tags/Attribute für das gesendete ``body_html`` (Driver-Entscheidung #1). */
export const EDITOR_ALLOWED_TAGS = [
  "strong",
  "b",
  "em",
  "i",
  "ul",
  "ol",
  "li",
  "a",
  "p",
  "br",
];
export const EDITOR_ALLOWED_ATTR = ["href"];

/** HTML-Text-Escaping (identisch zur früheren ``escapeSigHtml``-Semantik). */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Inline-Markdown → HTML: Links, dann Fett, dann Kursiv (auf escaptem Text). */
function inlineMd(s: string): string {
  let out = escapeHtml(s);
  // [text](url) VOR Fett/Kursiv, damit ``*`` in einer URL nicht als Kursiv greift.
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_m, text: string, url: string) => `<a href="${url}">${text}</a>`
  );
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // Fix ggü. altem ``bodyToHtml``: einfaches ``*x*`` ist Kursiv (<em>), nicht Fett.
  out = out.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  return out;
}

/**
 * Markdown-ish Text → gewhitelistetes HTML. Absätze durch Leerzeilen getrennt,
 * ``- ``-Zeilen werden zu ``<ul><li>``, einzelne Zeilenumbrüche zu ``<br>``.
 * Erzeugt AUSSCHLIESSLICH Whitelist-Tags → idempotent unter ``sanitizeEditorHtml``.
 */
export function mdToHtml(md: string): string {
  if (!md) return "";
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  const parts: string[] = [];
  let para: string[] = [];
  let list: string[] = [];
  const flushPara = () => {
    if (para.length) {
      parts.push(`<p>${para.map(inlineMd).join("<br>")}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      parts.push(`<ul>${list.map((li) => `<li>${inlineMd(li)}</li>`).join("")}</ul>`);
      list = [];
    }
  };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) {
      flushPara();
      flushList();
      continue;
    }
    const m = line.match(/^\s*-\s+(.*)$/);
    if (m) {
      flushPara();
      list.push(m[1]);
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return parts.join("");
}

/**
 * Vor-Normalisierung des rohen contentEditable-HTML VOR der DOMPurify-Whitelist:
 * ``execCommand`` verpackt Folgezeilen browserabhängig in ``<div>…</div>`` — jede
 * Div-Grenze ist ein sichtbarer Zeilenwechsel → in ``<br>`` übersetzen (sonst
 * gingen die Umbrüche beim Whitelisten verloren, weil ``div`` nicht erlaubt ist).
 */
export function preNormalizeBlocks(raw: string): string {
  let s = raw;
  s = s.replace(/<div[^>]*>/gi, "<br>").replace(/<\/div>/gi, "");
  s = s.replace(/&nbsp;/gi, " ");
  // Führendes <br> (aus einer ersten umschließenden Div) ist Rauschen.
  s = s.replace(/^(?:\s*<br\s*\/?>\s*)+/i, "");
  return s;
}

/**
 * Deterministische Tag-Normalisierung NACH dem Whitelisten (Driver-Entscheidung
 * #4): ``execCommand`` liefert je nach Browser ``<b>``/``<i>`` → auf ``<strong>``/
 * ``<em>`` vereinheitlichen, damit die Abnahme (<strong>/<em>) deterministisch ist.
 */
export function normalizeInlineTags(html: string): string {
  return html
    .replace(/<b>/gi, "<strong>")
    .replace(/<\/b>/gi, "</strong>")
    .replace(/<i>/gi, "<em>")
    .replace(/<\/i>/gi, "</em>");
}

/** ``true``, wenn das HTML keinen sichtbaren Text (nur Tags/Whitespace) enthält. */
export function isBlankHtml(h: string): boolean {
  if (!h) return true;
  const text = h
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, "");
  return text.length === 0;
}

/** Rohe Link-Eingabe → sichere absolute URL (http/https/mailto). */
export function normalizeUrl(u: string): string {
  const t = u.trim();
  if (!t) return "";
  if (/^(https?:|mailto:)/i.test(t)) return t;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) return `mailto:${t}`;
  return `https://${t}`;
}

/**
 * Signatur-Splice auf BEREITS gewhitelistetem HTML (Driver-Entscheidung #1):
 * das ERSTE literale ``[Name]`` durch die (vertrauenswürdige) ``sigHtml`` ersetzen;
 * ohne ``[Name]`` wird die Signatur angehängt. Weitere ``[Name]`` werden gestrippt —
 * in KEINEM Fall bleibt ein rohes ``[Name]`` stehen (Semantik wie zuvor
 * ``buildClipboard``). Die HTML-Hälften werden NICHT separat sanitisiert (ein Split
 * könnte Tags zerreißen).
 */
export function spliceSignature(cleanHtml: string, sigHtml: string | null): string {
  const NAME = "[Name]";
  const sig = sigHtml ?? "";
  const idx = cleanHtml.indexOf(NAME);
  if (idx >= 0) {
    const before = cleanHtml.slice(0, idx);
    const after = cleanHtml.slice(idx + NAME.length).split(NAME).join("");
    return `${before}${sig}${after}`;
  }
  return `${cleanHtml}${sig}`;
}

/**
 * Verabschiedung (``SignatureBlockResponse.closing``, zwei Zeilen durch ``\n``
 * getrennt) → Editor-HTML für den Abschlussblock im Composer (Task 57).
 *
 * Bewusst NICHT das ``html``-Feld derselben Antwort: das trägt die gebrandete
 * Signatur mit ``table``/``img``/``span style``, und davon überlebt NICHTS die
 * Editor-Whitelist — gemessen, nicht hergeleitet
 * (``docs/evidence/task-57/whitelist-sonde.md``). Die Verabschiedung dagegen ist
 * whitelist-rein und bleibt unter ``sanitizeEditorHtml`` unverändert; nur deshalb
 * darf sie im Editor stehen, wo der Nutzer sie bearbeiten und löschen kann.
 */
export function closingToHtml(closing: string): string {
  const lines = (closing || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return "";
  return `<p>${lines.map(escapeHtml).join("<br>")}</p>`;
}
